// Parallel per-document extraction fetch, client-side, on mount/docs-change.
// See design Section 6.2.
//
// No bulk endpoint exists — GET /documents/{doc.id}/extraction is fired once
// per extraction-eligible uploaded doc (estimates/invoices), via
// Promise.all. Fail-open: a failed/404 fetch for one doc must not block the
// others or crash rendering (matches DocSlot's runQualityCheck posture in
// the pre-refactor ClaimDocumentsTab.tsx).

import { useEffect, useState } from 'react';
import type { DocRecord, ExtractionSummary } from '../types';
import { computeMismatch } from './useAmountMismatch';
import type { RequestContext } from '../../media/requestContext';
import { fetchWithAuthRetry } from '@/lib/auth/fetch-with-auth-retry';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

// Extraction-capable doc types — the Documents tab only offers the OCR
// Extraction UI for these; mirrors EXTRACTION_DOC_TYPES in DocumentRow.tsx
// and the invoice/estimate + DocOCRModal eligibility gates in
// ClaimDocumentsTab.tsx — keep all three in sync. Docs of any other type
// never get an extraction-status fetch, so their row shows the plain
// Uploaded/Pending badge instead of Extracted/Processing/Failed.
export const EXTRACTION_DOC_TYPES = new Set([
  'repair_invoice_1', 'repair_invoice_2', 'repair_invoice_3',
  'dl_front', 'dl_back',
  'rc_front', 'rc_back',
  'policy_copy', 'claims_form',
]);

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  const userType = localStorage.getItem('user_type') || '';
  return {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantId,
    'X-User-Type': userType,
  };
}

async function fetchOneExtraction(
  jobId: string,
  docId: string,
  context?: RequestContext,
): Promise<ExtractionSummary | null> {
  try {
    const base = context ? context.baseUrl : `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}`;
    const url = `${base}/documents/${docId}/extraction`;
    const res = await fetchWithAuthRetry(url, { headers: context ? context.headers : authHeaders() });
    if (!res.ok) return null; // fail open — 404/other errors just omit this doc's summary
    const data = await res.json();
    const raw = (data?.extracted_data ?? {}) as Record<string, unknown>;

    const fields: Record<string, string> = {};
    Object.entries(raw).forEach(([k, v]) => {
      if (v != null && v !== 'null' && v !== '') fields[k] = String(v);
    });

    const mismatch = computeMismatch({
      parts_cost: fields.parts_cost ?? '',
      labor_charges: fields.labor_charges ?? '',
      tax_amount: fields.tax_amount ?? '',
      discount_amount: fields.discount_amount ?? '',
      total_amount: fields.total_amount ?? '',
    });

    return {
      confidence: typeof data?.confidence_score === 'number' ? data.confidence_score : null,
      fields,
      mismatch,
      ocrStatus: data?.ocr_status ?? null,
      ocrAttempts: typeof data?.ocr_attempts === 'number' ? data.ocr_attempts : 0,
      ocrLastError: data?.ocr_last_error ?? null,
    };
  } catch {
    return null; // fail open — never let one doc's network error block the rest
  }
}

export interface UseDocumentExtractionsResult {
  extractions: Map<string, ExtractionSummary>;
  loading: boolean;
}

export function useDocumentExtractions(
  jobId: string,
  docs: DocRecord[],
  context?: RequestContext,
): UseDocumentExtractionsResult {
  const [extractions, setExtractions] = useState<Map<string, ExtractionSummary>>(new Map());
  const [loading, setLoading] = useState(false);

  // Stable dependency: doc ids of extraction-eligible uploaded docs.
  const eligibleIds = docs.filter(d => EXTRACTION_DOC_TYPES.has(d.doc_type)).map(d => d.id).join(',');

  useEffect(() => {
    let cancelled = false;
    const eligible = docs.filter(d => EXTRACTION_DOC_TYPES.has(d.doc_type));

    if (eligible.length === 0) {
      setExtractions(new Map());
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all(
      eligible.map(async doc => {
        const summary = await fetchOneExtraction(jobId, doc.id, context);
        return [doc.id, summary] as const;
      }),
    ).then(results => {
      if (cancelled) return;
      const next = new Map<string, ExtractionSummary>();
      results.forEach(([id, summary]) => {
        if (summary) next.set(id, summary);
      });
      setExtractions(next);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, eligibleIds, context]);

  return { extractions, loading };
}
